import { mentionsIn, mentionText } from "../shared/mentions";
import { agentsCallable, callAgent, callsAgent } from "./agent-threads";
import { agentRequest, askAgentPending } from "./ask-agent";
import {
  Circle,
  Spline,
  Waypoints,
  BrickWall,
  DoorOpen,
  PanelsTopLeft,
  CornerUpLeft,
  Move,
} from "lucide-react";
import {
  applyDrawingDefaults,
  initializeDrawingDefaults,
  drawingDefaults,
} from "./drawing-defaults";
import { getPreferences } from "./preferences";
import { unitsPerMeter } from "../shared/mark-presentation";
import { toolSettings } from "./tools";
import { containingTrace } from "./tracing";
import { useTraceAnnotation } from "./useTraceAnnotation";
import { useFileImports } from "./useFileImports";
import {
  type DrawingSource,
  sourceName,
  scaleSource,
  sourceMeters,
} from "./source-scale";
import { emptyReview, type Person } from "./ReviewControls";
import { api } from "./api";
import { requestMake3d } from "./MakeThreeD";
import { reviewDraft } from "../shared/updates";
import { orderedSheets } from "./sheet-operations";
import {
  canEdit as roleCanEdit,
  canComment as roleCanComment,
} from "../shared/authorization";
import { useCallback, useEffect, useRef, useState } from "react";

import { useValue } from "@tldraw/state-react";
import { GeoShapeGeoStyle, createComment, createCommentThread, toRichText, type TLRichText, type TLCommentThread, type TLComment, type TLShape, type TLShapeId, type TLImageShape } from "@tldraw/tlschema";
import type { Editor, VecLike } from "./canvas";

import {
  StickyNote,
  MessageSquare,
  MousePointer2,
  Hand,
  Pencil,
  Slash,
  Hexagon,
  Type,
  Square,
  MoveUpRight,
  Highlighter,
} from "lucide-react";
import {
  AngleIcon,
  DimensionIcon,
  HatchedAreaIcon,
  RevisionCloudIcon,
} from "./ArchitectToolIcons";
import { type Sheet, type Point } from "../shared/schema";
import { exportMetrics } from "../shared/geometry";
import { sheetFor } from "./shapes";
import { controllers } from "./tools";
import { notify, type User, type Room } from "./api";
import {
  commentRegionPageBounds,
  commentPageRegionRecord,
  commentRegionRecord,
  type CommentRegion,
  type PendingRegionComment,
} from "./comment-regions";
import { applyToolCursor } from "./tool-cursors";

type CalibrationDraft = { sheet: DrawingSource; a: Point; b: Point };
type PendingComment =
  { kind: "point"; shape: TLShape | null; p: Point } | PendingRegionComment;
export function useWorkspace({
  editor,
  room,
  user,
  connected,
  busy,
  setBusy,
  back,
}: {
  editor: Editor;
  room: Room;
  user: User;
  connected: boolean;
  busy: string;
  setBusy: (s: string) => void;
  back: () => void;
}) {
  const annotation = useTraceAnnotation(editor);
  useEffect(() => {
    const prefs = getPreferences(user.id);
    initializeDrawingDefaults(
      editor,
      prefs.drawingColor,
      prefs.measurementUnit,
    );
  }, [editor, user.id]);
  const [pickingTrace, setPickingTrace] = useState<TLShapeId | null>(null);
  const shapes = useValue("shapes", () => editor.getCurrentPageShapes(), [
      editor,
    ]),
    selected = useValue("selection", () => editor.getSelectedShapes(), [
      editor,
    ]),
    tool = useValue(
      "tool",
      () =>
        editor.getCurrentToolId() === "geo" &&
        editor.getStyleForNextShape(GeoShapeGeoStyle) === "cloud"
          ? "cloud"
          : editor.getCurrentToolId(),
      [editor],
    ),
    zoom = useValue("zoom", () => editor.getZoomLevel(), [editor]),
    collaborators = useValue("people", () => editor.getCollaborators(), [
      editor,
    ]);
  const [people, setPeople] = useState<Person[]>([]);
  const [peopleError,setPeopleError] = useState("");
  const [peopleRefresh,setPeopleRefresh] = useState(0);
  const retryPeople = () => setPeopleRefresh(n=>n+1);
  const [review, setReview] = useState(emptyReview);
  useEffect(() => applyToolCursor(editor, tool), [editor, tool]);
  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(
          'input, textarea, select, [contenteditable="true"], [role="dialog"], [role="listbox"]',
        )
      )
        return;
      if (editor.getEditingShapeId() || editor.getCurrentToolId() === "select")
        return;
      // Let the active tool cancel its unfinished gesture before leaving it.
      editor.cancel();
      annotation.end();
      editor.setCurrentTool("select");
      editor.focus();
    };
    window.addEventListener("keydown", escape);
    return () => window.removeEventListener("keydown", escape);
  }, [editor]);

  useEffect(() => {
    if (tool === "select") editor.setOpacityForNextShapes(1);
  }, [editor, tool]);
  useEffect(() => {
    if (room.platform !== "cloudflare") return;
    let live = true;
    let inFlight = false;
    let failures = 0;
    let nextRefresh = 0;
    const refresh = () => {
      if (document.hidden || navigator.onLine === false || inFlight) return;
      inFlight = true;
      void api<Person[]>(
        `/api/rooms/${room.id}/people`,
        undefined,
        AbortSignal.timeout(15_000),
      )
        .then((p) => {
          failures = 0;
          nextRefresh = 0;
          if (live) {
            setPeople(p);
            setPeopleError("");
          }
        })
        .catch(() => {
          failures = Math.min(failures + 1, 4);
          nextRefresh =
            Date.now() + Math.min(120_000, 15_000 * 2 ** (failures - 1));
          if (live) {
            setPeople([]);
            setPeopleError("Could not load project members");
          }
        })
        .finally(() => {
          inFlight = false;
        });
    };
    refresh();
    const timer = setInterval(() => {
      if (Date.now() >= nextRefresh) refresh();
    }, 15000);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("online", refresh);
    return () => {
      live = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("online", refresh);
    };
  }, [room.id, peopleRefresh]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const sheets = orderedSheets(editor);
  const selectedSheet = sheetFor(editor, selected[0]);
  const active =
    selectedSheet || sheets.find((s) => s.id === activeId) || sheets[0];
  useEffect(() => {
    if (selectedSheet) setActiveId(selectedSheet.id);
  }, [selectedSheet?.id]);
  const records = useValue("comments", () => editor.store.allRecords(), [
      editor,
    ]),
    threads = records.filter(
      (r): r is TLCommentThread =>
        r.typeName === "comment-thread" && !r.isDeleted,
    ),
    comments = records.filter(
      (r): r is TLComment => r.typeName === "comment" && !r.isDeleted,
    );
  const [panel, setPanel] = useState<
    "drawings" | "comments" | "details" | null
  >(null);
  const [pointCount, setPointCount] = useState(0);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  // After calibrating: reopen a measuring tool, or continue Make 3D on that drawing.
  const calibrationReturnTool = useRef<"dimension" | "area" | "make3d" | null>(null);
  const make3dAfterScale = useRef<string | null>(null);
  const [scaleRequest, setScaleRequest] = useState<{
    source: DrawingSource;
    tool: "dimension" | "area" | "make3d";
  } | null>(null);
  const requestScale = useCallback(
    (source: DrawingSource, measurementTool: "dimension" | "area" | "make3d") => {
      editor.setCurrentTool("select");
      editor.select(source.id);
      setScaleRequest({ source, tool: measurementTool });
    },
    [editor],
  );
  const previousTool = useRef(tool);
  const [tab, setTab] = useState<"drawings" | "comments">("drawings"),
    [modal, setModal] = useState<"share" | "export" | null>(null),
    [cal, setCal] = useState<CalibrationDraft | null>(null),
    [meters, setMeters] = useState(""),
    [color, setColor] = useState("#d65331"),
    [length, setLength] = useState(""),
    [pending, setPending] = useState<PendingComment | null>(null),
    [commentAreaDraft, setCommentAreaDraft] = useState<{
      target: TLShape;
      region: CommentRegion;
      stroke: Point[];
    } | null>(null),
    [message, setMessage] = useState<TLRichText>(toRichText("")),
    [reply, setReply] = useState<TLRichText>(toRichText("")),
    [threadId, setThreadId] = useState<string | null>(null);
  const [shareRole, setShareRole] = useState("editor"),
    [shareUrl, setShareUrl] = useState(""),
    [paper, setPaper] = useState<"A3" | "A4">("A3"),
    [scale, setScale] = useState("100"),
    [landscape, setLandscape] = useState(true);
  const canEdit = roleCanEdit(room.role),
    canComment = roleCanComment(room.role);

  useEffect(() => {
    if (
      tool === "calibrate" &&
      (previousTool.current === "dimension" || previousTool.current === "area")
    )
      calibrationReturnTool.current = previousTool.current;
    previousTool.current = tool;
  }, [tool]);
  const imports = useFileImports(editor, room, user.id, connected);
  const fileInput = useRef<HTMLInputElement>(null),
    imageInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    editor.registerExternalContentHandler("files", ({ files, point }: { files: File[]; point?: VecLike }) => {
      if (!canEdit || editor.getIsReadonly()) return;
      if (modal) {
        notify("Close the current dialog before adding files.");
        return;
      }
      imports.add(files, point);
    });
    return () => {
      editor.registerExternalContentHandler("files", null);
    };
  }, [editor, canEdit, modal, imports.add]);
  useEffect(() => {
    controllers.set(editor, {
      requestScale,
      progress: (count, start) => {
        setPointCount(count);
        setStartPoint(start || null);
      },
      calibrate: (sheet, a, b) => {
        setCal({ sheet, a, b });
        setMeters("");
      },
      comment: (shape, p) => {
        if (canComment) {
          setPending({ kind: "point", shape, p });
          setTab("comments");
          setPanel(null);
          setThreadId(null);
          setMessage(toRichText(""));
        }
      },
      previewCommentArea: (target, region, stroke) => {
        setCommentAreaDraft(
          target && region && stroke ? { target, region, stroke } : null,
        );
      },
      commentArea: (target, region, stroke) => {
        if (canComment) {
          setCommentAreaDraft(null);
          setPending({ kind: "region", target, region, stroke });
          setTab("comments");
          setPanel(null);
          setThreadId(null);
          setMessage(toRichText(""));
        }
      },
      askAgent: () => {
        // Editors with the agents feature only; for anyone else Cmd+K does nothing.
        if (!canEdit || !agentsCallable() || editor.getIsReadonly()) return;
        setPending(askAgentPending(editor));
        setTab("comments");
        setPanel(null);
        setThreadId(null);
        setMessage(agentRequest());
        setReview(emptyReview());
      },
    });
    return () => {
      controllers.delete(editor);
    };
  }, [editor, canComment, canEdit, requestScale]);
  const cropping = useValue("crop mode", () => editor.getCroppingShapeId(), [
    editor,
  ]);
  const cropMark = useRef<string | null>(null);
  const startCrop = (shape: TLImageShape) => {
    cropMark.current = editor.markHistoryStoppingPoint("Crop image", { hold: true, checkpoint: true });
    editor.setCroppingShape(shape.id);
    editor.setCurrentTool("select.crop.idle");
    editor.focus();
  };
  const finishCrop = (cancel = false) => {
    if (cropMark.current) {
      if (cancel) editor.bailToMark(cropMark.current);
      else editor.commitHistoryStoppingPoint(cropMark.current);
    }
    cropMark.current = null;
    editor.setCurrentTool("select");
    editor.setCroppingShape(null);
    editor.focus();
  };
  useEffect(() => {
    if (!cropping && cropMark.current) {
      editor.commitHistoryStoppingPoint(cropMark.current);
      cropMark.current = null;
    }
  }, [cropping, editor]);
  useEffect(() => {
    toolSettings.length = Math.max(0, Number(length) || 0);
  }, [editor, length, tool]);
  const choose = (id: string) => {
    if (id === "dimension" || id === "area") {
      const source = scaleSource(editor, editor.getOnlySelectedShape()) || active;
      if (source && !sourceMeters(editor, source)) {
        requestScale(source, id);
        return;
      }
    }
    if (["select", "text", "note", "hand"].includes(id))
      editor.setOpacityForNextShapes(1);
    if (
      [
        "draw",
        "line",
        "arrow",
        "geo",
        "cloud",
        "highlight",
        "dimension",
        "area",
        "angle",
        "polygon",
      ].includes(id)
    )
      applyDrawingDefaults(editor, id);
    // The ordinary toolbar chooses the tool; tracing only supplies its destination.
    if (["select", "pin", "comment-area", "calibrate"].includes(id))
      annotation.end();
    else if (id !== "hand") {
      const paper = containingTrace(
        editor,
        editor.getOnlySelectedShape() ?? undefined,
      );
      if (paper && paper.id !== annotation.trace?.id)
        annotation.begin(paper.id);
    }
    if (!["select", "hand"].includes(id)) setPanel(null);
    if (["pin", "comment-area"].includes(id)) editor.selectNone();
    if (id === "cloud") {
      editor.setStyleForNextShapes(GeoShapeGeoStyle, "cloud");
      editor.setCurrentTool("geo");
    } else {
      if (id === "geo")
        editor.setStyleForNextShapes(GeoShapeGeoStyle, "rectangle");
      editor.setCurrentTool(id);
    }
    editor.focus();
  };
  const focusSheet = (sheet: Sheet) => {
    setActiveId(sheet.id);
    editor.setCurrentTool("select");
    editor.select(sheet.id);
    editor.zoomToBounds(editor.getShapePageBounds(sheet)!, {
      inset: editor.getViewportScreenBounds().w > 760 ? 300 : 80,
      animation: { duration: 200 },
    });
  };
  const focusThread = useCallback(
    (thread: TLCommentThread) => {
      setThreadId(thread.id);
      setReply(toRichText(""));
      if (thread.anchor.type === "point") {
        editor.setCurrentTool("select");
        editor.centerOnPoint(thread.anchor, { animation: { duration: 200 } });
        return true;
      }
      if (thread.anchor.type !== "shape") {
        const bounds = commentRegionPageBounds(editor, thread);
        if (!bounds) return false;
        editor.setCurrentTool("select");
        editor.zoomToBounds(bounds, {
          inset: editor.getViewportScreenBounds().w > 760 ? 280 : 90,
          animation: { duration: 200 },
        });
        return true;
      }
      const shape = editor.getShape(thread.anchor.shapeId);
      if (!shape) return false;
      const sheet = sheetFor(editor, shape);
      if (sheet) setActiveId(sheet.id);
      editor.setCurrentTool("select");
      editor.select(shape.id);
      const bounds = editor.getShapePageBounds(shape);
      if (bounds)
        editor.zoomToBounds(bounds, {
          inset: editor.getViewportScreenBounds().w > 760 ? 280 : 90,
          animation: { duration: 200 },
        });
      editor.setHintingShapes([shape.id]);
      window.setTimeout(() => editor.setHintingShapes([]), 900);
      return true;
    },
    [editor],
  );
  const finishCalibration = (applied: boolean) => {
    setCal(null);
    setMeters("");
    const next = applied ? calibrationReturnTool.current : null;
    calibrationReturnTool.current = null;
    const make3d = make3dAfterScale.current;
    make3dAfterScale.current = null;
    if (next === "make3d") {
      editor.setCurrentTool("select");
      if (make3d) requestMake3d(make3d);
    } else if (next) {
      applyDrawingDefaults(editor, next);
      editor.setCurrentTool(next);
      editor.focus();
    }
  };
  const beginRequestedCalibration = () => {
    if (!scaleRequest) return;
    calibrationReturnTool.current = scaleRequest.tool;
    make3dAfterScale.current = scaleRequest.tool === "make3d" ? scaleRequest.source.id : null;
    editor.select(scaleRequest.source.id);
    setScaleRequest(null);
    choose("calibrate");
  };
  const cancelScaleRequest = () => {
    setScaleRequest(null);
    calibrationReturnTool.current = null;
    make3dAfterScale.current = null;
    choose("select");
  };
  const addComment = () => {
    if (!pending || !mentionText(message).trim() || mentionText(message).length > 3000) return;
    const target = pending.kind === "point" ? pending.shape : pending.target;
    const region =
      pending.kind !== "region"
        ? null
        : pending.target
          ? commentRegionRecord(
              editor,
              pending.target,
              pending.region,
              pending.stroke,
            )
          : commentPageRegionRecord(pending.region);
    const thread = createCommentThread({
      anchor:
        region?.anchor ||
        (pending.kind === "point" && !target
          ? { type: "point", x: pending.p.x, y: pending.p.y }
          : {
              type: "shape",
              shapeId: target!.id,
              isPrecise: true,
              x:
                pending.kind === "point"
                  ? pending.p.x / editor.getShapeGeometry(target!).bounds.w
                  : 0.5,
              y:
                pending.kind === "point"
                  ? pending.p.y / editor.getShapeGeometry(target!).bounds.h
                  : 0.5,
            }),
      createdBy: user.id,
      pageId: editor.getCurrentPageId(),
      meta: {
        revision: target?.type === "sheet" ? target.props.revision : "01",
        ...region?.meta,
      },
    });
    try {
      reviewDraft(review);
    } catch (e) {
      notify((e as Error).message);
      return;
    }
    editor.markHistoryStoppingPoint("Add comment");
    const comment = createComment({
      authorId: user.id,
      body: message,
      pageId: editor.getCurrentPageId(),
      threadId: thread.id,
      meta: {
        author: user.name,
        ...(room.platform === "cloudflare"
          ? { review: JSON.parse(JSON.stringify({...review, mentions:[...new Set(mentionsIn(message).map(m=>m.userId))]})) }
          : {}),
      },
    });
    editor.store.put([thread, comment]);
    if (callsAgent(message, thread.id)) void callAgent(thread.id, comment.id);
    setThreadId(thread.id);
    setMessage(toRichText(""));
    setReview(emptyReview());
    setPending(null);
  };
  const addReply = (thread: TLCommentThread) => {
    if (!mentionText(reply).trim() || mentionText(reply).length > 3000) return;
    try {
      reviewDraft(review);
    } catch (e) {
      notify((e as Error).message);
      return;
    }
    editor.markHistoryStoppingPoint("Reply to discussion");
    const comment = createComment({
      authorId: user.id,
      body: reply,
      pageId: thread.pageId,
      threadId: thread.id,
      meta: {
        author: user.name,
        ...(room.platform === "cloudflare"
          ? { review: JSON.parse(JSON.stringify({...review, mentions:[...new Set(mentionsIn(reply).map(m=>m.userId))]})) }
          : {}),
      },
    });
    editor.store.put([comment]);
    if (callsAgent(reply, thread.id)) void callAgent(thread.id, comment.id);
    setReply(toRichText(""));
    setReview(emptyReview());
  };
  useEffect(() => {
    const target = new URLSearchParams(location.search).get("thread");
    if (!target || !connected) return;
    const thread = threads.find((t) => t.id === target);
    if (!thread) return;
    setPanel(focusThread(thread) ? null : "comments");
    setTab("comments");
    const url = new URL(location.href);
    url.searchParams.delete("thread");
    history.replaceState(history.state, "", url);
  }, [connected, threads, editor, room, focusThread]);
  const textOf = mentionText;
  let metrics: ReturnType<typeof exportMetrics> | undefined;
  try {
    if (active)
      metrics = exportMetrics(
        active.props.w,
        active.props.h,
        active.props.metersPerUnit,
        Number(scale),
        paper,
        landscape,
        !active.props.source,
      );
  } catch {}
  const toolList = [
    { id: "move", icon: Move, label: "Move" },
    { id: "polyline", icon: Waypoints, label: "Polyline" },
    { id: "circle", icon: Circle, label: "Circle" },
    { id: "curve", icon: Spline, label: "Curve" },
    { id: "wall", icon: BrickWall, label: "Wall" },
    { id: "door", icon: DoorOpen, label: "Door" },
    { id: "window", icon: PanelsTopLeft, label: "Window" },
    { id: "leader", icon: CornerUpLeft, label: "Leader" },
    { id: "select", icon: MousePointer2, label: "Select" },
    { id: "hand", icon: Hand, label: "Pan" },
    { id: "line", icon: Slash, label: "Line" },
    { id: "dimension", icon: DimensionIcon, label: "Dimension" },
    { id: "area", icon: HatchedAreaIcon, label: "Hatched area" },
    { id: "angle", icon: AngleIcon, label: "Angle" },
    { id: "polygon", icon: Hexagon, label: "Polygon / filled area" },
    { id: "cloud", icon: RevisionCloudIcon, label: "Revision cloud" },
    { id: "highlight", icon: Highlighter, label: "Highlighter" },
    { id: "eraser", icon: Pencil, label: "Erase whole objects" },
    { id: "draw", icon: Pencil, label: "Freehand" },
    { id: "geo", icon: Square, label: "Rectangle" },
    { id: "text", icon: Type, label: "Text" },
    { id: "note", icon: StickyNote, label: "Sticky note" },
    { id: "arrow", icon: MoveUpRight, label: "Arrow" },
    { id: "pin", icon: MessageSquare, label: "Point comment" },
    { id: "comment-area", icon: Highlighter, label: "Area" },
  ];
  return {
    annotation,
    pickingTrace,
    setPickingTrace,
    cropping,
    startCrop,
    finishCrop,
    people,
    peopleError,
    retryPeople,
    review,
    setReview,
    editor,
    room,
    user,
    connected,
    busy,
    setBusy,
    back,
    shapes,
    selected,
    tool,
    zoom,
    collaborators,
    sheets,
    active,
    records,
    threads,
    comments,
    panel,
    setPanel,
    pointCount,
    startPoint,
    tab,
    setTab,
    modal,
    setModal,
    cal,
    setCal,
    meters,
    setMeters,
    color,
    setColor,
    length,
    setLength,
    pending,
    setPending,
    commentAreaDraft,
    message,
    setMessage,
    reply,
    setReply,
    threadId,
    setThreadId,
    shareRole,
    setShareRole,
    shareUrl,
    setShareUrl,
    paper,
    setPaper,
    scale,
    setScale,
    landscape,
    setLandscape,
    imports,
    fileInput,
    imageInput,
    openFilePicker: () => fileInput.current?.click(),
    openImagePicker: () => imageInput.current?.click(),
    canEdit,
    canComment,
    choose,
    focusSheet,
    focusThread,
    finishCalibration,
    scaleRequest,
    requestScale,
    beginRequestedCalibration,
    cancelScaleRequest,
    addComment,
    addReply,
    textOf,
    metrics,
    toolList,
  };
}
export type WorkspaceModel = ReturnType<typeof useWorkspace>;
